const SIZE_BRACKET_REGEX = /\[([^\]]+)\]/;
const SIZE_STRIP_REGEX = /\s*\[[^\]]*\]\s*/g;

export const formatSizeValue = (value) => {
  if (value == null || value === '') return '';
  const str = String(value).trim();
  if (!str) return '';
  const stripped = str.replace(/,/g, '');
  if (/^-?\d+(\.\d+)?$/.test(stripped)) {
    const num = Number(stripped);
    if (Number.isFinite(num)) return num.toLocaleString('en-US');
  }
  return str;
};

export const parseSizeFromItemText = (itemText = '') => {
  const match = String(itemText).match(SIZE_BRACKET_REGEX);
  if (!match || !match[1]) return '';
  return formatSizeValue(match[1]);
};

export const removeSizeFromItemText = (itemText = '') => {
  return String(itemText).replace(SIZE_STRIP_REGEX, '').trim() || '-';
};

export const getFontSizeForLength = (maxLen = 0) => {
  const len = Number(maxLen) || 0;
  if (len <= 18) return 8.5;
  if (len <= 26) return 8;
  if (len <= 35) return 7.5;
  if (len <= 45) return 7;
  if (len <= 60) return 6.5;
  if (len <= 80) return 6;
  return 5.5;
};

const CURRENCY_SYMBOLS = { 'USD': '$', 'IDR': 'Rp', 'KRW': '₩' };

export const getCurrencySymbol = (currency) => CURRENCY_SYMBOLS[currency] || '$';

export const formatDateForPrint = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-CA');
};

export const formatCurrencyValue = (amount, currency) => {
  if (!amount && amount !== 0) return '-';
  return Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
};

export const formatCurrencyWithSymbol = (amount, currency) => {
  if (!amount && amount !== 0) return { symbol: getCurrencySymbol(currency), value: '-' };
  return {
    symbol: getCurrencySymbol(currency),
    value: formatCurrencyValue(amount, currency)
  };
};

export const makeTdStyle = (fontSize, text, align = 'center') => {
  const len = String(text || '').length;
  const padding = '3px';
  const wrap = len > 60 ? 'white-space:normal;word-break:break-word;overflow-wrap:break-word' : 'white-space:nowrap';
  return `border:1px solid #000;padding:${padding};font-size:${fontSize}px;text-align:${align};min-height:18px;${wrap}`;
};

export const makeCellStyle = (fontSize, _text, align = 'center') => {
  const base = {
    border: '1px solid #000',
    padding: '2px 3px',
    fontSize: `${fontSize}px`,
    textAlign: align,
    fontWeight: 'normal',
    fontFamily: '"Malgun Gothic", "맑은 고딕", sans-serif',
    minHeight: '18px',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    lineHeight: 1.2
  };
  return base;
};

export const TH_STYLE = Object.freeze({
  border: '1px solid #000',
  padding: '3px',
  fontSize: '8px',
  textAlign: 'center',
  fontWeight: 'normal',
  fontFamily: '"Malgun Gothic", "맑은 고딕", sans-serif',
  whiteSpace: 'nowrap',
});

export const TH_STYLE_STRING = 'border:1px solid #000;padding:3px;font-size:8px;text-align:center;font-weight:normal;white-space:nowrap';

const v = (val) => String(val || '-');

export const extractItemTexts = (item, invoice, visibleColumns, sym) => {
  const texts = [];
  if (visibleColumns.managerName !== false) texts.push(v(item.manager_name || invoice.manager_name));
  if (visibleColumns.buyer !== false) texts.push(v(item.buyer || invoice.buyer_name));
  if (visibleColumns.styleNo !== false) texts.push(v(item.style_no || item.style_name));
  if (visibleColumns.fileNo !== false) texts.push(v(item.file_no || invoice.file_no));
  if (visibleColumns.factory !== false) texts.push(v(item.factory));
  if (visibleColumns.deliveryDate !== false) texts.push(formatDateForPrint(item.delivery_date || item.date));
  if (visibleColumns.item !== false) texts.push(removeSizeFromItemText(item.thread_name || item.item_description || ''));
  if (visibleColumns.size !== false) texts.push(parseSizeFromItemText(item.thread_name || item.item_description || '') || formatSizeValue(item.size_name || item.size) || '-');
  if (visibleColumns.color !== false) texts.push(v(item.color));
  if (visibleColumns.qty !== false) texts.push(item.quantity ? Number(item.quantity).toLocaleString() : '-');

  const vatAmount = Number(item.vat_amount || 0) + Number(item.vat_amount2 || 0);
  if (visibleColumns.price !== false) {
    texts.push((item.price || item.unit_price) ? sym + ' ' + formatCurrencyValue(item.price || item.unit_price) : '-');
  }
  if (visibleColumns.vat !== false) {
    texts.push(vatAmount ? sym + ' ' + formatCurrencyValue(vatAmount) : '-');
  }
  if (visibleColumns.amount !== false) {
    texts.push(item.amount ? sym + ' ' + formatCurrencyValue(item.amount) : '-');
  }
  return texts;
};

export const computeUniformFontSize = (items, invoice, visibleColumns) => {
  const sym = getCurrencySymbol(invoice.currency);
  let maxLen = 0;
  for (let i = 0; i < items.length; i++) {
    const texts = extractItemTexts(items[i], invoice, visibleColumns, sym);
    for (let j = 0; j < texts.length; j++) {
      if (texts[j].length > maxLen) maxLen = texts[j].length;
    }
  }
  return getFontSizeForLength(maxLen);
};

export const computeInvoiceTotals = (items) => {
  let totalQty = 0, subTotal = 0, totalVat = 0;
  for (let i = 0; i < items.length; i++) {
    totalQty += Number(items[i].quantity || 0);
    subTotal += Number(items[i].amount || 0);
    totalVat += Number(items[i].vat_amount || 0) + Number(items[i].vat_amount2 || 0);
  }
  return { totalQty, subTotal, totalVat };
};

export const COLUMN_OPTIONS = Object.freeze([
  { id: 'managerName', label: 'MANAGER' },
  { id: 'buyer', label: 'BUYER' },
  { id: 'styleNo', label: 'STYLE NO' },
  { id: 'fileNo', label: 'FILE NO' },
  { id: 'factory', label: 'FACTORY' },
  { id: 'deliveryDate', label: 'DELIVERY DATE' },
  { id: 'item', label: 'ITEM' },
  { id: 'size', label: 'SIZE' },
  { id: 'color', label: 'COLOR' },
  { id: 'qty', label: 'QTY' },
  { id: 'price', label: 'PRICE' },
  { id: 'vat', label: 'VAT' },
  { id: 'amount', label: 'AMOUNT' }
]);

export const imageToBase64DataUrl = async (imagePath) => {
  const response = await fetch(imagePath);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

export const imageToBase64Raw = async (imagePath) => {
  try {
    const dataUrl = await imageToBase64DataUrl(imagePath);
    return dataUrl.split(',')[1] || null;
  } catch {
    return null;
  }
};
